import React, { useState, useRef } from 'react';

export default function StoryCreator({ onClose, onCreate }) {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [textColor, setTextColor] = useState('#ffffff');
  const [textSize, setTextSize] = useState(24);
  const [textX, setTextX] = useState(50);
  const [textY, setTextY] = useState(50);
  const [textRotation, setTextRotation] = useState(0);
  const [emojiContent, setEmojiContent] = useState('');
  const [emojiSize, setEmojiSize] = useState(40);
  const [emojiX, setEmojiX] = useState(50);
  const [emojiY, setEmojiY] = useState(60);
  const [emojiRotation, setEmojiRotation] = useState(0);
  const [bgColor, setBgColor] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [dragKind, setDragKind] = useState(null);
  const fileInputRef = useRef(null);
  const contentRef = useRef(null);

  const COLOR_PALETTE = ['#ffffff', '#000000', '#e94560', '#4CAF50', '#2196F3', '#FFC107', '#FF5722', '#9C27B0', '#00BCD4', '#FFEB3B'];
  const EMOJI_LIST = ['😀', '😍', '😂', '😎', '😭', '😡', '👍', '👎', '❤️', '🔥', '⭐', '🎉', '💯', '🎵', '🚀', '🌈', '⚡', '✅', '❌', '❓', '👀', '🙏', '😴', '🤔'];

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
      setBgColor(null);
    }
  };

  const handleMouseDown = (kind) => (e) => {
    if (kind === 'text' && !textContent) return;
    if (kind === 'emoji' && !emojiContent) return;
    e.stopPropagation();
    setDragKind(kind);
    setDragging(true);
    handleMove(kind)(e);
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    handleMove(dragKind)(e);
  };

  const handleMouseUp = () => setDragging(false);

  const handleMove = (kind) => (e) => {
    if (!contentRef.current) return;
    const rect = contentRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    if (kind === 'text') {
      setTextX(x);
      setTextY(y);
    } else {
      setEmojiX(x);
      setEmojiY(y);
    }
  };

  const handleSubmit = () => {
    if (!image && !bgColor) return;
    const formData = new FormData();
    if (image) formData.append('file', image);
    formData.append('textContent', textContent);
    formData.append('textColor', textColor);
    formData.append('textSize', textSize.toString());
    formData.append('textX', textX.toString());
    formData.append('textY', textY.toString());
    formData.append('textRotation', textRotation.toString());
    formData.append('emojiContent', emojiContent);
    formData.append('emojiX', emojiX.toString());
    formData.append('emojiY', emojiY.toString());
    formData.append('emojiSize', emojiSize.toString());
    formData.append('emojiRotation', emojiRotation.toString());
    if (bgColor) formData.append('bgColor', bgColor);
    onCreate(formData);
  };

  return (
    <div className="story-creator-overlay" onClick={onClose}>
      <div className="story-creator" onClick={(e) => e.stopPropagation()}>
        <div className="story-creator-header">
          <h3>Новая история</h3>
          <button onClick={onClose}>✕</button>
        </div>
        <div className="story-bg-row">
          <span>Фон:</span>
          {['#1a1a2e', '#16213e', '#0f3460', '#e94560', '#4CAF50', '#2196F3', '#FFC107', '#FF5722', '#9C27B0', '#00BCD4', '#f5f5f5'].map((c) => (
            <button key={c} type="button" className={`story-color-chip ${bgColor === c ? 'active' : ''}`}
              style={{ background: c, border: bgColor === c ? '2px solid #2196F3' : '1px solid #ccc' }}
              onClick={() => {
                if (bgColor === c) { setBgColor(null); return; }
                setBgColor(c); setImage(null); setImagePreview(null);
              }} />
          ))}
        </div>
        {!imagePreview && !bgColor ? (
          <div className="story-upload-area" onClick={() => fileInputRef.current?.click()}>
            <div className="story-upload-icon">📷</div>
            <p>Нажмите чтобы выбрать фото</p>
            <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageSelect} style={{ display: 'none' }} />
          </div>
        ) : (
          <>
            <div className="story-preview" ref={contentRef}
              onMouseDown={handleMouseDown('text')} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
              {bgColor ? (
                <div className="story-preview-image" style={{ background: bgColor }} />
              ) : (
                <img src={imagePreview} alt="" className="story-preview-image" />
              )}
              {textContent && (
                <div className={`story-text-overlay editable ${dragKind === 'text' ? 'active' : ''}`} style={{
                  color: textColor,
                  fontSize: `${textSize}px`,
                  left: `${textX}%`,
                  top: `${textY}%`,
                  transform: `translate(-50%, -50%) rotate(${textRotation}deg)`,
                  cursor: 'move',
                }} onMouseDown={handleMouseDown('text')}>
                  {textContent}
                </div>
              )}
              {emojiContent && (
                <div className={`story-emoji-overlay editable ${dragKind === 'emoji' ? 'active' : ''}`} style={{
                  fontSize: `${emojiSize}px`,
                  left: `${emojiX}%`,
                  top: `${emojiY}%`,
                  transform: `translate(-50%, -50%) rotate(${emojiRotation}deg)`,
                  cursor: 'move',
                }} onMouseDown={handleMouseDown('emoji')}>
                  {emojiContent}
                </div>
              )}
            </div>
            <div className="story-controls">
              <div className="story-control-group">
                <label>Текст</label>
                <input type="text" value={textContent} onChange={(e) => setTextContent(e.target.value)}
                  placeholder="Введите текст..." className="story-text-input" />
              </div>
              {textContent && (
                <>
                  <div className="story-control-row">
                    <div className="story-control-group">
                      <label>Цвет текста</label>
                      <div className="story-color-palette">
                        {COLOR_PALETTE.map((c) => (
                          <button key={c} type="button"
                            className={`story-color-chip ${textColor === c ? 'active' : ''}`}
                            style={{ background: c, border: textColor === c ? '2px solid #2196F3' : '1px solid #ccc' }}
                            onClick={() => setTextColor(c)} />
                        ))}
                        <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)}
                          title="Свой цвет" style={{ width: 28, height: 28, border: 'none', padding: 0 }} />
                      </div>
                    </div>
                    <div className="story-control-group">
                      <label>Размер: {textSize}px</label>
                      <input type="range" min="12" max="72" value={textSize}
                        onChange={(e) => setTextSize(parseInt(e.target.value))} />
                    </div>
                  </div>
                  <div className="story-control-row">
                    <div className="story-control-group">
                      <label>Вращение: {textRotation}°</label>
                      <input type="range" min="-180" max="180" value={textRotation}
                        onChange={(e) => setTextRotation(parseInt(e.target.value))} />
                    </div>
                  </div>
                </>
              )}
              <div className="story-control-group">
                <label>Смайлик (отдельно от текста)</label>
                <input type="text" value={emojiContent} onChange={(e) => setEmojiContent(e.target.value)}
                  placeholder="Например: 🔥" className="story-text-input" />
              </div>
              <div className="story-emoji-list">
                {EMOJI_LIST.map((e) => (
                  <button key={e} type="button" className="story-emoji-btn" onClick={() => setEmojiContent(e)}>{e}</button>
                ))}
              </div>
              {emojiContent && (
                <>
                  <div className="story-control-row">
                    <div className="story-control-group">
                      <label>Размер смайлика: {emojiSize}px</label>
                      <input type="range" min="20" max="120" value={emojiSize}
                        onChange={(e) => setEmojiSize(parseInt(e.target.value))} />
                    </div>
                  </div>
                  <p className="story-hint">Перетаскивайте смайлик на изображении для перемещения</p>
                </>
              )}
              {textContent && (
                <p className="story-hint">Перетаскивайте текст на изображении для перемещения</p>
              )}
            </div>
            <div className="story-creator-actions">
              <button className="settings-btn" style={{ background: '#f0f0f0', color: '#333' }}
                onClick={() => { setImage(null); setImagePreview(null); setBgColor(null); }}>Убрать фото/фон</button>
              <button className="settings-btn primary" onClick={handleSubmit}>Опубликовать</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
